// src/pages/app-availability.ts
import { LitElement, html, css, TemplateResult, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  setDoc,
  Timestamp,
  onSnapshot,
type Unsubscribe,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { classMap } from 'lit/directives/class-map.js';
import { ensureActiveClub } from '../services/ensureActiveClub';

type TeamKey = 'sunday' | 'midweek' | 'wednesday';

type AvailabilityItem = {
  id: string;
  team: TeamKey;
  date: Date;
  opponentName: string;
  venueType?: string;
  format?: string | null;
  notes?: string | null;
  time?: string;
  availability?: boolean;
};
type AvailabilityCache = {
  activeSeasonId: string;
  clubId: string;
  playerId: string;
  items: Array<Omit<AvailabilityItem, 'date'> & { dateISO: string }>;
  cachedAt: number;
};

@customElement('app-availability')
export class AppAvailability extends LitElement {
  @state() private items: AvailabilityItem[] = [];
  @state() private loading = true;
  @state() private error = '';
  @state() private activeSeasonId = '2026';
  @state() private clubId = 'appletoncc';
  @state() private playerId = '';
  @state() private teamFilter: 'all' | TeamKey = 'all';
  @state() private visibleCount = 5;
  private db = getFirestore();
  private auth = getAuth();
private availabilityUnsubs: Unsubscribe[] = [];
static styles = css`
  :host {
    display: block;
    min-height: 100vh;
    color: var(--text);
    background:
      radial-gradient(900px 500px at 10% -10%, rgba(32,110,58,0.10), transparent 55%),
      linear-gradient(180deg, var(--bg), #f4fbf6);
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .page {
    max-width: 900px;
    margin: 0 auto;
    padding: 14px 14px 96px;
  }

  .hero {
    padding: 18px 4px 14px;
  }

  .eyebrow {
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 6px;
    font-weight: 700;
  }

  h1 {
    margin: 0;
    font-size: 28px;
    line-height: 1.05;
    letter-spacing: -0.8px;
    color: var(--text);
  }

  .desc {
    margin-top: 8px;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.45;
  }

  .filters {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 10px 0 12px;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(12px);
  }

  .chip {
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text);
    padding: 9px 12px;
    border-radius: 999px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: 0 4px 14px rgba(0,0,0,0.05);
  }

  .chip.active {
    background: var(--primary);
    border-color: var(--primary);
    color: var(--primary-contrast);
    font-weight: 800;
  }

  .list {
    display: grid;
    gap: 12px;
    margin-top: 6px;
  }

.card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.92);
  border-radius: 20px;
  padding: 14px 14px 14px 22px;
  box-shadow: var(--shadow);
}

.card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 7px;
  background: rgba(24, 51, 36, 0.16);
}

.card.type-t20::before { background: #206e3a; }
.card.type-o40::before { background: #2563eb; }
.card.type-o35::before { background: #f59e0b; }
.card.type-cup::before { background: #a21caf; }
.card.type-interclub::before { background: #ef4444; }
.card.type-other::before { background: rgba(24, 51, 36, 0.22); }

  .topline {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
  }

  .load-more {
  width: 100%;
  margin: 16px 0 0;
  border: 1px solid rgba(32,110,58,0.22);
  border-radius: 16px;
  padding: 14px 16px;
  background: var(--primary);
  color: var(--primary-contrast);
  font-weight: 850;
  cursor: pointer;
  box-shadow: var(--shadow);
}

  .date {
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 5px;
    font-weight: 700;
  }

  .opponent {
    font-size: 17px;
    font-weight: 850;
    letter-spacing: -0.2px;
    color: var(--text);
  }

  .meta {
    margin-top: 7px;
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
    color: var(--muted);
    font-size: 12px;
  }

.pill.t20 {
  background: rgba(32,110,58,0.10);
  color: var(--primary);
  border-color: rgba(32,110,58,0.24);
}

.pill.o40 {
  background: rgba(37,99,235,0.10);
  color: #1d4ed8;
  border-color: rgba(37,99,235,0.18);
}

.pill.o35 {
  background: rgba(245,158,11,0.12);
  color: #b45309;
  border-color: rgba(245,158,11,0.22);
}

.pill.cup {
  background: rgba(162,28,175,0.10);
  color: #86198f;
  border-color: rgba(162,28,175,0.20);
}

.pill.interclub {
  background: rgba(239,68,68,0.10);
  color: #b91c1c;
  border-color: rgba(239,68,68,0.20);
}

.notes {
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(32,110,58,0.06);
  color: var(--muted);
  font-size: 12px;
  line-height: 1.45;
}

  .team {
    text-transform: capitalize;
  }

  .status {
    font-size: 11px;
    padding: 6px 9px;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--muted);
    white-space: nowrap;
    font-weight: 800;
  }

  .status.yes {
    border-color: rgba(32,110,58,0.35);
    background: rgba(32,110,58,0.10);
    color: var(--primary);
  }

  .status.no {
    border-color: rgba(211,47,47,0.35);
    background: rgba(211,47,47,0.10);
    color: var(--error);
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 9px;
    margin-top: 14px;
  }

  button {
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 12px 10px;
    color: var(--text);
    background: #ffffff;
    font-weight: 850;
    cursor: pointer;
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }

  button.available.active {
    background: var(--primary);
    border-color: var(--primary);
    color: var(--primary-contrast);
  }

  button.unavailable.active {
    background: var(--error);
    border-color: var(--error);
    color: #ffffff;
  }

  .empty,
  .loading,
  .error {
    margin-top: 16px;
    padding: 18px;
    border-radius: 18px;
    border: 1px solid var(--border);
    background: rgba(255,255,255,0.92);
    color: var(--muted);
    text-align: center;
    box-shadow: var(--shadow);
  }

  .error {
    border-color: rgba(211,47,47,0.35);
    background: rgba(211,47,47,0.08);
    color: var(--error);
  }

  @media (min-width: 720px) {
    .list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    h1 {
      font-size: 34px;
    }
  }
`;

 connectedCallback(): void {
  super.connectedCallback();
  window.addEventListener('app-availability-changed', this.availabilityChangedHandler);
  this.fetchItems();
}

disconnectedCallback(): void {
  super.disconnectedCallback();
  this.availabilityUnsubs.forEach((u) => u());
  this.availabilityUnsubs = [];
}

  private cacheKey(userId: string) {
  return `availability_cache_${this.clubId}_${this.activeSeasonId}_${userId}`;
}

private bindAvailabilityListeners() {
  this.availabilityUnsubs.forEach((u) => u());
  this.availabilityUnsubs = [];

  for (const item of this.items) {
    const ref = doc(
      this.db,
      `Clubs/${this.clubId}/Seasons/${this.activeSeasonId}/Teams/${item.team}/Fixtures/${item.id}/Availability/${this.playerId}`
    );

    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;

      const data = snap.data() as any;
      const availability =
        data.availability === true
          ? true
          : data.availability === false
            ? false
            : undefined;

      this.items = this.items.map((x) =>
        x.id === item.id && x.team === item.team
          ? { ...x, availability }
          : x
      );

      const user = this.auth.currentUser;
      if (user) this.saveAvailabilityCache(user.uid);
    });

    this.availabilityUnsubs.push(unsub);
  }
}

private loadAvailabilityCache(userId: string) {
  try {
    const raw = localStorage.getItem(this.cacheKey(userId));
    if (!raw) return false;

    const cache = JSON.parse(raw) as AvailabilityCache;

    this.activeSeasonId = cache.activeSeasonId || this.activeSeasonId;
    this.clubId = cache.clubId || this.clubId;
    this.playerId = cache.playerId || this.playerId;

    this.items = (cache.items || []).map((item) => ({
      ...item,
      date: new Date(item.dateISO),
    }));

    return this.items.length > 0;
  } catch {
    return false;
  }
}

private saveAvailabilityCache(userId: string) {
  try {
    const cache: AvailabilityCache = {
      activeSeasonId: this.activeSeasonId,
      clubId: this.clubId,
      playerId: this.playerId,
      items: this.items.map((item) => ({
        ...item,
        dateISO: item.date.toISOString(),
      })),
      cachedAt: Date.now(),
    };

    localStorage.setItem(this.cacheKey(userId), JSON.stringify(cache));
  } catch {
    // ignore storage issues
  }
}

  private async fetchItems(): Promise<void> {
    this.loading = true;
    this.error = '';

    try {
      const user = this.auth.currentUser;
      if (!user) {
        this.error = 'Please sign in to set your availability.';
        return;
      }
this.clubId = (await ensureActiveClub()) || 'appletoncc';

const userSnap = await getDoc(doc(this.db, `Users/${user.uid}`));
this.activeSeasonId =
  (userSnap.exists() ? (userSnap.data() as any).activeSeasonId : null) || '2026';

const cacheLoaded = this.loadAvailabilityCache(user.uid);
if (cacheLoaded) {
  this.loading = false;
}

      const memberSnap = await getDoc(doc(this.db, `Clubs/${this.clubId}/Members/${user.uid}`));
      this.playerId =
        (memberSnap.exists() ? (memberSnap.data() as any).playerId : null) || user.uid;

      const teams: TeamKey[] = ['sunday', 'midweek'];
      const results: AvailabilityItem[] = [];

      for (const team of teams) {
        const fixturesRef = collection(
          this.db,
          `Clubs/${this.clubId}/Seasons/${this.activeSeasonId}/Teams/${team}/Fixtures`
        );

        const fixturesQuery = query(
          fixturesRef,
          where('date', '>=', Timestamp.fromDate(new Date(new Date().setHours(0, 0, 0, 0)))),
          orderBy('date', 'asc')
        );

        const snap = await getDocs(fixturesQuery);

        for (const fixtureDoc of snap.docs) {
          const data = fixtureDoc.data() as any;
          const date = this.toDate(data.date);

          const availability = await this.getAvailability(fixtureDoc.id, team);

        results.push({
  id: fixtureDoc.id,
  team,
  date,
  opponentName:
    data.opponentName ||
    data.opponent ||
    data.opposition ||
    data.title ||
    'Opponent TBC',
  venueType: data.venueType || data.venue || '',
  format: data.format || null,
  notes: data.notes || null,
  time: data.time || data.startTime || '',
  availability,
});
        }
      }

      this.items = results.sort((a, b) => a.date.getTime() - b.date.getTime());
      this.saveAvailabilityCache(user.uid);
      this.bindAvailabilityListeners();
    } catch (err: any) {
      console.error('Error fetching availability:', err);
      this.error = err?.message || 'Could not load availability.';
    } finally {
      this.loading = false;
    }
  }

  private async getAvailability(fixtureId: string, team: TeamKey): Promise<boolean | undefined> {
    const primaryRef = doc(
      this.db,
      `Clubs/${this.clubId}/Seasons/${this.activeSeasonId}/Teams/${team}/Fixtures/${fixtureId}/Availability/${this.playerId}`
    );

    const primarySnap = await getDoc(primaryRef);
    if (primarySnap.exists()) return primarySnap.data().availability;

    // Fallback because older/newer data sometimes says wednesday instead of midweek.
    if (team === 'midweek') {
      const fallbackRef = doc(
        this.db,
        `Clubs/${this.clubId}/Seasons/${this.activeSeasonId}/Teams/wednesday/Fixtures/${fixtureId}/Availability/${this.playerId}`
      );
      const fallbackSnap = await getDoc(fallbackRef);
      if (fallbackSnap.exists()) return fallbackSnap.data().availability;
    }

    return undefined;
  }

  private async setAvailability(item: AvailabilityItem, availability: boolean): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    const ref = doc(
      this.db,
      `Clubs/${this.clubId}/Seasons/${this.activeSeasonId}/Teams/${item.team}/Fixtures/${item.id}/Availability/${this.playerId}`
    );

    await setDoc(
      ref,
      {
        availability,
        playerId: this.playerId,
        userId: user.uid,
        team: item.team,
        fixtureId: item.id,
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );

    this.items = this.items.map((x) =>
      x.id === item.id && x.team === item.team ? { ...x, availability } : x
    );
    this.saveAvailabilityCache(user.uid);
    this.emitAvailabilityChanged({
  fixtureId: item.id,
  teamId: item.team,
  playerId: this.playerId,
  availability,
});
  }

  private availabilityChangedHandler = (event: Event) => {
  const detail = (event as CustomEvent).detail as {
    fixtureId: string;
    teamId: string;
    playerId: string;
    availability: boolean;
  };

  if (!detail || detail.playerId !== this.playerId) return;

  this.items = this.items.map((item) =>
    item.id === detail.fixtureId && item.team === detail.teamId
      ? { ...item, availability: detail.availability }
      : item
  );

  const user = this.auth.currentUser;
  if (user) this.saveAvailabilityCache(user.uid);
};

  private toDate(value: any): Date {
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value === 'string') return new Date(value);
    return new Date();
  }

  private emitAvailabilityChanged(detail: {
  fixtureId: string;
  teamId: string;
  playerId: string;
  availability: boolean;
}) {
  window.dispatchEvent(
    new CustomEvent('app-availability-changed', {
      detail,
    })
  );
}

private filteredItems() {
  const filtered = this.items.filter((item) => {
    if (this.teamFilter === 'all') return true;
    return item.team === this.teamFilter;
  });

  return filtered.slice(0, this.visibleCount);
}

private totalFilteredItems() {
  return this.items.filter((item) => {
    if (this.teamFilter === 'all') return true;
    return item.team === this.teamFilter;
  }).length;
}
  private formatDate(date: Date) {
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  }

  render(): TemplateResult {
    const items = this.filteredItems();

    return html`
      <app-header pageTitle="Availability"></app-header>

      <main class="page">
        <section class="hero">
          <div class="eyebrow">Season ${this.activeSeasonId}</div>
          <h1>Set your availability</h1>
          <div class="desc">
            Let the captains know whether you can play. Tap once to update each fixture.
          </div>
        </section>

        <div class="filters">
          ${this.filterChip('all', 'All')}
          ${this.filterChip('sunday', 'Sunday')}
          ${this.filterChip('midweek', 'Midweek')}
        </div>

        ${this.loading
          ? html`<div class="loading">Loading fixtures…</div>`
          : this.error
            ? html`<div class="error">${this.error}</div>`
            : items.length
              ? html`
                 <section class="list">
  ${items.map((item) => this.renderFixtureCard(item))}
</section>

${this.visibleCount < this.totalFilteredItems()
  ? html`
      <button
        class="load-more"
        @click=${() => (this.visibleCount += 5)}
      >
        Load more fixtures
      </button>
    `
  : nothing}
                `
              : html`
                  <div class="empty">
                    No upcoming fixtures found for this season.
                  </div>
                `}
      </main>
    `;
  }

 private filterChip(filter: 'all' | TeamKey, label: string) {
  return html`
    <button
      class=${classMap({
        chip: true,
        active: this.teamFilter === filter,
      })}
      @click=${() => {
        this.teamFilter = filter;
        this.visibleCount = 5;
      }}
    >
      ${label}
    </button>
  `;
}

  private norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

private fixtureType(item: AvailabilityItem): 'interclub' | 'cup' | 'o40' | 'o35' | 't20' | 'other' {
  const opp = this.norm(item.opponentName);
  const fmt = this.norm(item.format);

  if (opp.includes('interclub') || opp.includes('president')) return 'interclub';
  if (fmt.includes('cup') || opp.includes('cup')) return 'cup';
  if (fmt === '40' || fmt.includes('40')) return 'o40';
  if (fmt === '35' || fmt.includes('35')) return 'o35';
  if (fmt.includes('t20')) return 't20';

  return 'other';
}

private fixtureLabel(type: ReturnType<AppAvailability['fixtureType']>): string {
  switch (type) {
    case 'interclub':
      return 'Interclub / Presidents';
    case 'cup':
      return 'Cup';
    case 'o40':
      return '40 overs';
    case 'o35':
      return '35 overs';
    case 't20':
      return 'T20';
    default:
      return 'Other';
  }
}

private teamLabel(team: TeamKey): string {
  if (team === 'midweek' || team === 'wednesday') return 'Midweek';
  return 'Sunday';
}

  private renderFixtureCard(item: AvailabilityItem) {
  const type = this.fixtureType(item);

  const statusClass = {
    status: true,
    yes: item.availability === true,
    no: item.availability === false,
  };

  const statusText =
    item.availability === true
      ? 'Available'
      : item.availability === false
        ? 'Not available'
        : 'Not set';

  return html`
    <article class="card type-${type}">
      <div class="topline">
        <div>
          <div class="date">${this.formatDate(item.date)}</div>
          <div class="opponent">${item.opponentName}</div>
        </div>
        <div class=${classMap(statusClass)}>${statusText}</div>
      </div>

      <div class="meta">
        <span class="pill team">${this.teamLabel(item.team)}</span>

        ${item.venueType
          ? html`<span class="pill">${item.venueType}</span>`
          : nothing}

        <span class="pill ${type}">${this.fixtureLabel(type)}</span>

        ${item.time
          ? html`<span class="pill">${item.time}</span>`
          : nothing}
      </div>

      ${item.notes
        ? html`<div class="notes">${item.notes}</div>`
        : nothing}

      <div class="actions">
        <button
          class=${classMap({
            available: true,
            active: item.availability === true,
          })}
          @click=${() => this.setAvailability(item, true)}
        >
          Available
        </button>

        <button
          class=${classMap({
            unavailable: true,
            active: item.availability === false,
          })}
          @click=${() => this.setAvailability(item, false)}
        >
          Not Available
        </button>
      </div>
    </article>
  `;
}
}